import { isAfter, isSameDay, isValid, parseISO,startOfDay, subDays } from "date-fns";
import { useMemo } from "react";

interface ConversationWithDates {
  readonly id: any;
  readonly title: string;
  readonly updatedAt: any;
  readonly createdAt: any;
}

interface CategorizedConversations {
  todayConversations: ConversationWithDates[];
  yesterdayConversations: ConversationWithDates[];
  previous7DaysConversations: ConversationWithDates[];
  olderConversations: ConversationWithDates[];
}

export const useConversationCategories = (conversations: ConversationWithDates[]): CategorizedConversations => {
  return useMemo(() => {
    // Use date-fns to handle dates correctly
    const today = startOfDay(new Date());
    const yesterday = startOfDay(subDays(new Date(), 1));
    const sevenDaysAgo = startOfDay(subDays(new Date(), 7));

    // Group conversations
    const todayConversations: ConversationWithDates[] = [];
    const yesterdayConversations: ConversationWithDates[] = [];
    const previous7DaysConversations: ConversationWithDates[] = [];
    const olderConversations: ConversationWithDates[] = [];

    // Sort conversations into their respective groups based on updated_at date
    conversations.forEach((conversation) => {
      try {
        // Parse the timestamp from the conversation
        const updatedAt = parseISO(conversation.updatedAt);

        // Check if date is valid
        if (!isValid(updatedAt)) {
          olderConversations.push(conversation);
          return;
        }

        // Compare using date-fns functions
        if (isSameDay(updatedAt, today)) {
          todayConversations.push(conversation);
        } else if (isSameDay(updatedAt, yesterday)) {
          yesterdayConversations.push(conversation);
        } else if (isAfter(updatedAt, sevenDaysAgo) || isSameDay(updatedAt, sevenDaysAgo)) {
          previous7DaysConversations.push(conversation);
        } else {
          olderConversations.push(conversation);
        }
      } catch {
        // Default to older conversations if there's an error
        olderConversations.push(conversation);
      }
    });

    // Sort each category by updatedAt in descending order (newest first)
    const sortByUpdatedAt = (a: ConversationWithDates, b: ConversationWithDates) => {
      const dateA = parseISO(a.updatedAt);
      const dateB = parseISO(b.updatedAt);
      return dateB.getTime() - dateA.getTime();
    };

    return {
      todayConversations: todayConversations.sort(sortByUpdatedAt),
      yesterdayConversations: yesterdayConversations.sort(sortByUpdatedAt),
      previous7DaysConversations: previous7DaysConversations.sort(sortByUpdatedAt),
      olderConversations: olderConversations.sort(sortByUpdatedAt)
    };
  }, [conversations]);
};